import { Injectable, Injector, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { filter, Subscription } from 'rxjs';
import { ConfirmDialogComponent } from 'app/shared/components/confirm-dialog/confirm-dialog.component';
import { WorkspaceTabsService } from 'app/layout/common/workspace-tabs/workspace-tabs.service';

/**
 * Back that stays inside MediAstra is unchanged.
 * The step that would open the page before the app (Chrome's start page / Google)
 * asks whether to log out. Stay keeps the session. Logout uses the normal sign-out path.
 *
 * The browser will not tell us the previous address, and once that navigation finishes
 * the app is already gone. A same-URL trap entry sits behind the first signed-in page
 * so that step still runs inside the app.
 */
@Injectable({ providedIn: 'root' })
export class LeaveAppConfirmService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly injector = inject(Injector);

  private armed = false;
  private prompting = false;
  private leaving = false;
  private currentUrl = '';
  private shellSub: Subscription | null = null;
  private readonly onPop = (event: PopStateEvent): void => this.handlePop(event);

  initEarlyListener(): void {
    if (this.shellSub || this.armed || typeof window === 'undefined') {
      return;
    }
    this.currentUrl = window.location.href;
    this.shellSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl = window.location.href;
        if (this.armed || !this.isSignedInShell(event.urlAfterRedirects)) {
          return;
        }
        this.arm();
      });
  }

  private isSignedInShell(url: string): boolean {
    if (!this.hasSession()) {
      return false;
    }
    const path = url.split('?')[0].split('#')[0];
    return !(
      path.startsWith('/auth') ||
      path.startsWith('/sign-in') ||
      path.startsWith('/sign-up') ||
      path.startsWith('/sign-out') ||
      path.startsWith('/forgot-password') ||
      path.startsWith('/reset-password') ||
      path.startsWith('/signed-in-redirect') ||
      path.startsWith('/unlock-session') ||
      path.startsWith('/unauthorized') ||
      path.startsWith('/on-boarding/sign-up')
    );
  }

  private hasSession(): boolean {
    try {
      return !!localStorage.getItem('accessToken');
    } catch {
      return false;
    }
  }

  /** Put a same-URL trap behind the current page. The next back that would leave lands on it. */
  private arm(): void {
    this.armed = true;
    const state = { ...(window.history.state ?? {}), medastra: true, medastraExitTrap: false };
    window.history.replaceState({ ...state, medastraExitTrap: true }, '');
    window.history.pushState({ ...state, medastraExitTrap: false }, '', window.location.href);
    this.currentUrl = window.location.href;
    window.addEventListener('popstate', this.onPop);
  }

  private handlePop(event: PopStateEvent): void {
    const nextUrl = window.location.href;
    const previousUrl = this.currentUrl;
    this.currentUrl = nextUrl;
    const state = event.state as { medastraExitTrap?: boolean } | null;
    if (!state?.medastraExitTrap) {
      return;
    }

    // Landed on an earlier page that is still this app. Keep the trap behind it.
    if (previousUrl !== nextUrl) {
      this.parkOnCurrentPage();
      return;
    }

    if (!this.hasSession()) {
      window.removeEventListener('popstate', this.onPop);
      window.history.back();
      return;
    }

    this.parkOnCurrentPage();
    if (this.prompting || this.leaving) {
      return;
    }
    this.prompting = true;
    this.zone.run(() => this.ask());
  }

  /** Current entry is the trap. Push a normal entry so the following back hits the trap again. */
  private parkOnCurrentPage(): void {
    const state = { ...(window.history.state ?? {}), medastra: true, medastraExitTrap: false };
    window.history.pushState(state, '', window.location.href);
    this.currentUrl = window.location.href;
  }

  private ask(): void {
    const dialog = this.injector.get(MatDialog);
    dialog
      .open(ConfirmDialogComponent, {
        width: '460px',
        disableClose: true,
        data: {
          title: 'Logout',
          message: 'Do you want to logout the application or not?',
          dialogType: 'warning',
          cancelText: 'No',
          confirmText: 'Yes',
          initialFocus: 'cancel',
          actionButtonsOnlyNavigation: true,
        },
      })
      .afterClosed()
      .subscribe((leave) => {
        this.prompting = false;
        if (leave !== true) {
          return;
        }
        const tabs = this.injector.get(WorkspaceTabsService);
        const router = this.injector.get(Router);
        void tabs.confirmSignOut().then((ok) => {
          if (!ok) {
            return;
          }
          this.leaving = true;
          window.removeEventListener('popstate', this.onPop);
          void router.navigate(['/sign-out']);
        });
      });
  }
}
